import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import ShadowBox from "../../layoutComponents/shadowBox/ShadowBox";
import { Button } from '@mui/material';
import "./codewarsShadowBox.scss";

interface CodewarsShadowBoxProps {
    userId?: string;
}

const CodewarsShadowBox: React.FC<CodewarsShadowBoxProps> = ({ userId }) => {
    const history = useHistory();
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [isOwnProfile, setIsOwnProfile] = useState<boolean>(false);
    const [currentUserUuid, setCurrentUserUuid] = useState<string | null>(null);

    useEffect(() => {
        // Get current logged-in user's UUID
        const fetchCurrentUser = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API}/users/profile`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                        },
                    }
                );
                
                if (response.ok) {
                    const data = await response.json();
                    setCurrentUserUuid(data.uuid);
                    
                    // Check if viewing own profile
                    setIsOwnProfile(data.uuid === userId);
                }
            } catch (error) {
                console.error('Error fetching current user:', error);
                setIsOwnProfile(false);
            }
        };

        if (userId) {
            fetchCurrentUser();
        } else {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        // Only check Codewars connection if it's the user's own profile
        const checkCodewarsConnection = async () => {
            if (!isOwnProfile || currentUserUuid !== userId) {
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API}/codewars/check-codewars`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
                        },
                    }
                );
                
                if (response.ok) {
                    const data = await response.json();
                    // User is connected if they have both username and webhook_id
                    setIsConnected(!!(data.username && data.webhook_id));
                }
            } catch (error) {
                console.error('Error checking Codewars connection:', error);
            } finally {
                setLoading(false);
            }
        };

        if (currentUserUuid !== null) {
            checkCodewarsConnection();
        }
    }, [isOwnProfile, currentUserUuid, userId]);

    const handleSyncClick = () => {
        history.push('/syncCodewarsAccount');
    };

    const handleBattingCageClick = () => {
        history.push('/battingcage');
    };

    // Don't render anything if viewing someone else's profile
    if (!isOwnProfile) {
        return null;
    }

    if (loading) {
        return (
            <ShadowBox title="Loading...">
                <div className="shadowbox-content">
                    <p>Loading account status...</p>
                </div>
            </ShadowBox>
        );
    }

    // If user has connected account, show Batting Cage prompt
    if (isConnected) {
        return (
            <ShadowBox title="Practice Your Skills">
                <div className="batting-cage-prompt">
                    <div className="batting-cage-prompt__image-container">
                        <img 
                            src="/battingcage_1.png" 
                            alt="Batting Cage" 
                            className="batting-cage-image"
                            onError={(e) => {
                                // Fallback if image doesn't exist
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    </div>
                    <Button
                        className="battingCageButton"
                        variant="contained"
                        color="primary"
                        onClick={handleBattingCageClick}
                    >
                       Batting Cage
                    </Button>
                </div>
            </ShadowBox>
        );
    }

    // If no connected account, show sync prompt
    return (
        <ShadowBox title="Sync Your Codewars Account">
            <div className="sync-prompt">
                <div className="sync-prompt__image-container">
                    <div className="sync-prompt__images-row left">
                        <img src="/Vector.png" alt="1" className="image-50" /> 
                    </div>
                    <div className="sync-prompt__images-row">
                        <img src="/Group 624916.png" alt="2" className="image-custom" /> 
                    </div>
                    <div className="sync-prompt__images-row right">
                        <img src="/Subtract4.png" alt="3" className="image-50" /> 
                    </div>
                </div>
                <p>
                    To get the most out of your experience, please sync your Codewars account with us.
                </p>
                <Button
                    className="syncButton"
                    variant="contained"
                    color="primary"
                    onClick={handleSyncClick}
                >
                    Sync Now
                </Button>
            </div>
        </ShadowBox>
    );
};

export default CodewarsShadowBox;